import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import ForegroundLoader from '../../../common/components/Loader/ForegroundLoader'
import fetchData from '../../../service/apiService'
import { endpointLabels, pillarNames } from '../../../common/utils/enum'
import {
  clone,
  formatDecimals,
  getEndpointFromConfig,
} from '../../../common/utils/helper'
import CircleRating from './CircleRating'
import { TOPIC_LIST } from '../../../common/utils/constants'

const TopicGrid = ({ countryProfileURLAuthor, countryProfileURLPublish }) => {
  const { endpointList, environment, apiKeyIncluded, apiKeyName, apiKeyValue } =
    useSelector((state) => state.common)
  const { selectedTopic, countryList } = useSelector(
    (state) => state.dataHomepage
  )

  const [isLoading, setIsLoading] = useState(false)
  const [gridData, setGridData] = useState([])
  const [availableColumns, setAvailableColumns] = useState([
    'pillar1',
    'pillar2',
    'pillar3',
  ])
  const [sortConfig, setSortConfig] = useState({
    key: 'economyName',
    order: 'asc',
  })

  useEffect(() => {
    const fetchTopicData = async () => {
      try {
        setIsLoading(true)
        const topicURL = `${
          getEndpointFromConfig(endpointList, endpointLabels.TopicPillars)
            ?.apiEndpoint
        }?Indicator=${selectedTopic}&Level=Topic`
        const pillarURL = `${
          getEndpointFromConfig(endpointList, endpointLabels.TopicPillars)
            ?.apiEndpoint
        }?Indicator=${selectedTopic}&Level=Pillar`
        let headers = undefined
        if (apiKeyIncluded === 'true') {
          headers = {
            [apiKeyName]: apiKeyValue,
          }
        }
        const response = await Promise.allSettled([
          fetchData(topicURL, 'GET', null, headers),
          fetchData(pillarURL, 'GET', null, headers),
        ])

        if (response && response.length > 0) {
          let topicData =
            response[0].status === 'fulfilled' ? clone(response[0].value) : []
          let pillarsData =
            response[1].status === 'fulfilled' ? clone(response[1].value) : []
          // form country list from both topicdata and pillardata
          let countries = []
          if (topicData && topicData.length > 0) {
            countries = topicData.map((item) => item.EconomyCode)
          }
          if (pillarsData && pillarsData.length > 0) {
            countries = [
              ...countries,
              ...pillarsData.map((item) => item.EconomyCode),
            ]
          }
          const countryList = [...new Set(countries)]
          let finalDataForGrid = []
          countryList.forEach((country) => {
            const countryTopicData = topicData.find(
              (topic) =>
                topic.EconomyCode.toLowerCase() === country.toLowerCase()
            )
            const countryPillarData = pillarsData.filter(
              (topic) =>
                topic.EconomyCode.toLowerCase() === country.toLowerCase()
            )
            const pillar1Data = countryPillarData?.find(
              (item) => item.InternalDPName.toLowerCase() === 'pillar1'
            )
            const pillar2Data = countryPillarData?.find(
              (item) => item.InternalDPName.toLowerCase() === 'pillar2'
            )
            const pillar3Data = countryPillarData?.find(
              (item) => item.InternalDPName.toLowerCase() === 'pillar3'
            )
            finalDataForGrid.push({
              economyCode: country,
              economyName: countryTopicData
                ? countryTopicData.EconomyName
                : countryPillarData
                ? countryPillarData[0].EconomyName
                : '',
              topicValue: countryTopicData?.DatapointValue
                ? formatDecimals(countryTopicData?.DatapointValue)
                : '',
              pillar1: pillar1Data?.DatapointValue
                ? formatDecimals(pillar1Data.DatapointValue)
                : '',
              pillar2: pillar2Data?.DatapointValue
                ? formatDecimals(pillar2Data.DatapointValue)
                : '',
              pillar3: pillar3Data?.DatapointValue
                ? formatDecimals(pillar3Data.DatapointValue)
                : '',
            })
          })
          // filter out countries which don't have any valid data for topic and pillar1 and pillar2 and pillar3
          finalDataForGrid = finalDataForGrid.filter(
            (item) =>
              item.topicValue || item.pillar1 || item.pillar2 || item.pillar3
          )
          finalDataForGrid.sort((a, b) =>
            a.economyName
              .toLowerCase()
              .localeCompare(b.economyName.toLowerCase())
          )
          setGridData(finalDataForGrid)
        }
      } catch (error) {
        console.error('Error fetching topicPillars:', error)
      } finally {
        setIsLoading(false)
      }
    }
    if (selectedTopic && endpointList.length > 0 && apiKeyIncluded) {
      fetchTopicData()
    }
  }, [selectedTopic, endpointList, apiKeyIncluded])

  const handleSorting = (key) => {
    let sortedData = [...gridData]
    if (key === 'economyName' || key === 'region' || key === 'incomeGroup') {
      if (sortConfig?.key === key && sortConfig?.order === 'asc') {
        sortedData.reverse()
        setSortConfig({ key, order: 'desc' })
      } else {
        sortedData.sort((a, b) =>
          a[key].toLowerCase().localeCompare(b[key].toLowerCase())
        )
        setSortConfig({ key, order: 'asc' })
      }
    } else {
      if (sortConfig?.key === key && sortConfig?.order === 'desc') {
        sortedData.sort((a, b) => {
          if (a[key] === null || a[key] === undefined || a[key] === '') return 1
          if (b[key] === null || b[key] === undefined || b[key] === '')
            return -1
          return Number(a[key]) - Number(b[key])
        })
        setSortConfig({ key, order: 'asc' })
      } else {
        sortedData.sort((a, b) => {
          if (a[key] === null || a[key] === undefined || a[key] === '') return 1
          if (b[key] === null || b[key] === undefined || b[key] === '')
            return -1
          return Number(b[key]) - Number(a[key])
        })
        setSortConfig({ key, order: 'desc' })
      }
    }
    setGridData(sortedData)
  }

  const tableRows =
    gridData &&
    gridData.length > 0 &&
    gridData.map((item) => {
      const countryData = countryList.find(
        (country) => country.id.toLowerCase() === item.economyCode.toLowerCase()
      )
      const topicData = TOPIC_LIST.find(
        (topic) => topic.name.toLowerCase() === selectedTopic.toLowerCase()
      )
      let redirectURL = ''
      if (environment === 'author') {
        redirectURL = `${countryProfileURLAuthor}.${countryData?.url}.html?topic=${topicData?.id}`
      } else {
        redirectURL = `${countryProfileURLPublish}/${countryData?.url}?topic=${topicData?.id}`
      }
      return (
        <tr key={`topic-row-${item.economyCode}`}>
          <td>
            <a href={redirectURL}>{item.economyName}</a>
          </td>
          {availableColumns.includes('pillar1') && (
            <td style={{ position: 'relative' }}>
              <CircleRating
                value={item.pillar1 / 10}
                countryCode={item.economyCode}
                pillar="pillar1"
                pillarValue={item.pillar1}
              />
            </td>
          )}
          {availableColumns.includes('pillar2') && (
            <td style={{ position: 'relative' }}>
              <CircleRating
                value={item.pillar2 / 10}
                countryCode={item.economyCode}
                pillar="pillar2"
                pillarValue={item.pillar2}
              />
            </td>
          )}
          {availableColumns.includes('pillar3') && (
            <td style={{ position: 'relative' }}>
              <CircleRating
                value={item.pillar3 / 10}
                countryCode={item.economyCode}
                pillar="pillar3"
                pillarValue={item.pillar3}
              />
            </td>
          )}
          <td>{item.topicValue}</td>
          {/* {showHiddenColumns && availableColumns.includes('region') && (
            <td>{item.region}</td>
          )}
          {showHiddenColumns && availableColumns.includes('incomeGroup') && (
            <td>{item.incomeGroup}</td>
          )} */}
        </tr>
      )
    })

  return (
    <div className="bready-table">
      {isLoading && <ForegroundLoader />}
      {selectedTopic && (
        <table>
          <thead>
            <tr>
              <th
                className={
                  sortConfig.key === 'economyName'
                    ? sortConfig.order === 'asc'
                      ? 'sort-asc'
                      : 'sort-desc'
                    : 'sort-default'
                }
                onClick={() => handleSorting('economyName')}
              >
                Economies
              </th>
              {availableColumns.includes('pillar1') && (
                <th
                  onClick={() => handleSorting('pillar1')}
                  className={
                    sortConfig.key === 'pillar1'
                      ? sortConfig.order === 'asc'
                        ? 'sort-asc'
                        : 'sort-desc'
                      : 'sort-default'
                  }
                >
                  {
                    pillarNames.find(
                      (item) => item.datapointName.toLowerCase() === 'pillar1'
                    )?.name
                  }
                </th>
              )}
              {availableColumns.includes('pillar2') && (
                <th
                  onClick={() => handleSorting('pillar2')}
                  className={
                    sortConfig.key === 'pillar2'
                      ? sortConfig.order === 'asc'
                        ? 'sort-asc'
                        : 'sort-desc'
                      : 'sort-default'
                  }
                >
                  {
                    pillarNames.find(
                      (item) => item.datapointName.toLowerCase() === 'pillar2'
                    )?.name
                  }
                </th>
              )}
              {availableColumns.includes('pillar3') && (
                <th
                  onClick={() => handleSorting('pillar3')}
                  className={
                    sortConfig.key === 'pillar3'
                      ? sortConfig.order === 'asc'
                        ? 'sort-asc'
                        : 'sort-desc'
                      : 'sort-default'
                  }
                >
                  {
                    pillarNames.find(
                      (item) => item.datapointName.toLowerCase() === 'pillar3'
                    )?.name
                  }
                </th>
              )}
              <th
                onClick={() => handleSorting('topicValue')}
                className={
                  sortConfig.key === 'topicValue'
                    ? sortConfig.order === 'asc'
                      ? 'sort-asc'
                      : 'sort-desc'
                    : 'sort-default'
                }
              >
                Topic Score
              </th>
            </tr>
          </thead>
          <tbody>{tableRows}</tbody>
        </table>
      )}
    </div>
  )
}
export default TopicGrid
